/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/disaEntriesApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API functions for DISA Entries module.
    All API calls are centralized here.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  DisaEntry,
  ListDisaEntriesParams,
  ListDisaEntriesResponse,
  CreateDisaEntryPayload,
  UpdateDisaEntryPayload,
  ClassOfServiceOption
} from '../types';

/**
 * Fetch list of DISA entries
 */
export async function fetchDisaEntries(
  params: ListDisaEntriesParams
): Promise<ListDisaEntriesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search })
  });

  // Add domain filter if provided
  if (params.domainId) {
    queryParams.append('domainId', params.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/telephony/disa-entries?${queryParams}`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch DISA entries');
  }

  const result = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch DISA entries');
  }

  const data = Array.isArray(result.data) ? result.data : [];
  const pagination = result.pagination || {};

  return {
    success: true,
    data,
    pagination: {
      page: pagination.page ?? params.page,
      limit: pagination.limit ?? params.limit,
      total: pagination.total ?? data.length,
      totalPages: pagination.totalPages ?? 0
    }
  };
}

/**
 * Fetch single DISA entry by ID
 */
export async function fetchDisaEntryById(id: string | number): Promise<DisaEntry> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/disa-entries/${id}`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch DISA entry');
  }

  const result = await response.json();

  if (!result?.success || !result?.data) {
    throw new Error(result?.message || 'Failed to fetch DISA entry');
  }

  return result.data;
}

/**
 * Create new DISA entry
 */
export async function createDisaEntry(
  payload: CreateDisaEntryPayload
): Promise<DisaEntry> {
  const response = await fetch(
    buildApiUrl('/api/telephony/disa-entries'),
    {
      method: 'POST',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    let message = 'Failed to create DISA entry';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }

  const result = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to create DISA entry');
  }

  return result.data;
}

/**
 * Update existing DISA entry
 */
export async function updateDisaEntry(
  id: string | number,
  payload: UpdateDisaEntryPayload
): Promise<DisaEntry> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/disa-entries/${id}`),
    {
      method: 'PUT',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    let message = 'Failed to update DISA entry';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }

  const result = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to update DISA entry');
  }

  return result.data;
}

/**
 * Delete DISA entry by ID
 */
export async function deleteDisaEntry(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/disa-entries/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    let message = 'Failed to delete DISA entry';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }
}

/**
 * Fetch Class of Services for dropdown
 */
export async function fetchClassOfServices(): Promise<ClassOfServiceOption[]> {
  const response = await fetch(
    buildApiUrl('/api/telephony/class-of-services?limit=1000'),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch Class of Services');
  }

  const result = await response.json();
  const items = Array.isArray(result?.data)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ? result.data.map((cos: any) => ({ id: cos.id, name: cos.name }))
    : [];

  return items;
}

